import {bpx, type BPX, space} from 'quickstart/theme'
import {defaultThemeVars} from 'quickstart/theme/css-modules'
import * as R from 'rambdax'
import type {CSSProperties} from 'react'
import {type Nullish, nullish} from 'tizra'

type Sys<T> = {[b in BPX]?: T | Nullish}
export type SysProp<T> = T | Nullish | Sys<T>
type SysProps<T> = {[K in keyof T]?: SysProp<T[K]>}
type OnlySys<T> = {[K in keyof T]?: Sys<T[K]>}

type CreateSystemHookProps<
  P extends object,
  S extends {vars: CSSProperties},
> = {
  propNames: readonly (keyof P)[]
  reducer: (state: S, b: BPX, values: OnlySys<P>) => S
}

const createSystemHook =
  <P extends Record<string, unknown>, S extends {vars: CSSProperties}>({
    propNames,
    reducer,
  }: CreateSystemHookProps<P, S>) =>
  (_props: SysProps<P>): CSSProperties => {
    const sysProps = propNames.reduce(
      (o, p) => {
        const v = _props[p]
        if (!nullish(v)) {
          o[p] = (typeof v === 'object' ? v : {_: v}) as Sys<P[keyof P]>
        }
        return o
      },
      {} as OnlySys<P>,
    )
    if (R.isEmpty(sysProps)) return {}
    const {vars} = bpx.reduce((state, b) => reducer(state, b, sysProps), {
      vars: {},
    } as S)
    return vars
  }

const themeVarSet = new Set(Object.keys(defaultThemeVars))

const simpleSystemHook = <K extends string, V extends string>(
  propName: K,
  varName = propName,
  themeName?: string,
) =>
  createSystemHook<Record<K, V>, {vars: CSSProperties; prev?: string}>({
    propNames: [propName],
    reducer: ({vars, prev}, b, values) => {
      const current = values[propName]?.[b]
      if (current && current !== prev) {
        if (b === '_') b = 'xs'
        const themeVar = themeName && `--eg-theme-${themeName}-${current}`
        // Mutation in a reducer, oh no!
        vars[`--eg-sys--${varName}--${b}`] =
          themeVar && themeVarSet.has(themeVar) ? `var(${themeVar})` : current
        prev = current
      }
      return {vars, prev}
    },
  })

const useDisplay = simpleSystemHook('display')
type UseDisplayProps = Parameters<typeof useDisplay>[0]

const useJustifyContent = simpleSystemHook('justify', 'justify-content')
type UseJustifyContentProps = Parameters<typeof useJustifyContent>[0]

const useAlignItems = simpleSystemHook('alignItems', 'align-items')
type UseAlignItemsProps = Parameters<typeof useAlignItems>[0]

const useAlignContent = simpleSystemHook('alignContent', 'align-content')
type UseAlignContentProps = Parameters<typeof useAlignContent>[0]

const marginProps = ['m', 'mx', 'my', 'mt', 'mr', 'mb', 'ml'] as const

type MarginProp = (typeof marginProps)[number]

const sp = (x: string | number | Nullish): string | Nullish => {
  if (typeof x === 'string') {
    return space[x as keyof typeof space] ?? x
  }
  if (typeof x === 'number') {
    return `${x}px`
  }
  return x
}

interface Margins {
  top?: string
  right?: string
  bottom?: string
  left?: string
}

const combinedMargin = ({top, right, bottom, left}: Margins): string => {
  if (!top && !right && !bottom && !left) return ''
  if (top === right && top === bottom && top === left) return top!
  return `${top || 0} ${right || 0} ${bottom || 0} ${left || 0}`
}

const useMargin = createSystemHook<
  {[k in MarginProp]?: number | string},
  {vars: CSSProperties; margins?: Margins; prev?: string}
>({
  propNames: marginProps,
  reducer: ({vars, margins, prev}, b, vs) => {
    margins = {
      top: sp(vs.mt?.[b] ?? vs.my?.[b] ?? vs.m?.[b]) || margins?.top,
      right: sp(vs.mr?.[b] ?? vs.mx?.[b] ?? vs.m?.[b]) || margins?.right,
      bottom: sp(vs.mb?.[b] ?? vs.my?.[b] ?? vs.m?.[b]) || margins?.bottom,
      left: sp(vs.ml?.[b] ?? vs.mx?.[b] ?? vs.m?.[b]) || margins?.left,
    }
    const margin = combinedMargin(margins)
    if (margin && margin !== prev) {
      if (b === '_') b = 'xs'
      vars[`--eg-sys--margin--${b}`] = margin
      prev = margin
    }
    return {vars, margins, prev}
  },
})

type UseMarginProps = Parameters<typeof useMargin>[0]

const paddingProps = ['p', 'px', 'py', 'pt', 'pr', 'pb', 'pl'] as const

type PaddingProp = (typeof paddingProps)[number]

const usePadding = createSystemHook<
  {[k in PaddingProp]?: number | string},
  {vars: CSSProperties; paddings?: Margins; prev?: string}
>({
  propNames: paddingProps,
  reducer: ({vars, paddings, prev}, b, vs) => {
    paddings = {
      top: sp(vs.pt?.[b] ?? vs.py?.[b] ?? vs.p?.[b]) || paddings?.top,
      right: sp(vs.pr?.[b] ?? vs.px?.[b] ?? vs.p?.[b]) || paddings?.right,
      bottom: sp(vs.pb?.[b] ?? vs.py?.[b] ?? vs.p?.[b]) || paddings?.bottom,
      left: sp(vs.pl?.[b] ?? vs.px?.[b] ?? vs.p?.[b]) || paddings?.left,
    }
    const padding = combinedMargin(paddings)
    if (padding && padding !== prev) {
      if (b === '_') b = 'xs'
      vars[`--eg-sys--padding--${b}`] = padding
      prev = padding
    }
    return {vars, paddings, prev}
  },
})

type UsePaddingProps = Parameters<typeof usePadding>[0]

const gapProps = ['gap', 'colGap', 'rowGap'] as const

type GapProp = (typeof gapProps)[number]

interface Gaps {
  col?: string
  row?: string
}

const combinedGap = ({row, col}: Gaps): string => {
  if (!row && !col) return ''
  if (row === col) return row!
  return `${row || 0} ${col || 0}`
}

const useGap = createSystemHook<
  {[k in GapProp]?: number | string},
  {vars: CSSProperties; gaps?: Gaps; prev?: string}
>({
  propNames: gapProps,
  reducer: ({vars, gaps, prev}, b, vs) => {
    gaps = {
      row: sp(vs.rowGap?.[b] ?? vs.gap?.[b]) || gaps?.row,
      col: sp(vs.colGap?.[b] ?? vs.gap?.[b]) || gaps?.col,
    }
    const gap = combinedGap(gaps)
    if (gap && gap !== prev) {
      if (b === '_') b = 'xs'
      vars[`--eg-sys--gap--${b}`] = gap
      prev = gap
    }
    return {vars, gaps, prev}
  },
})

type UseGapProps = Parameters<typeof useGap>[0]

const expandTrack = (x: string | number | Nullish): string | undefined => {
  if (typeof x === 'number') return `repeat(${x}, minmax(0, 1fr))`
  if (typeof x === 'string') return x
  return undefined
}

const useGridTemplateColumns = createSystemHook<
  {cols?: number | string},
  {vars: CSSProperties; prev?: string}
>({
  propNames: ['cols'],
  reducer: ({vars, prev}, b, vs) => {
    const current = expandTrack(vs.cols?.[b])
    if (current && current !== prev) {
      if (b === '_') b = 'xs'
      vars[`--eg-sys--grid-template-columns--${b}`] = current
      prev = current
    }
    return {vars, prev}
  },
})

type UseGridTemplateColumnsProps = Parameters<typeof useGridTemplateColumns>[0]

const flexFlowProps = ['dir', 'wrap'] as const

interface FlexFlow {
  dir?: 'column' | 'row'
  wrap?: 'wrap' | 'nowrap'
}

const combinedFlexFlow = ({dir, wrap}: FlexFlow): string => {
  if (!dir && !wrap) return ''
  return `${dir || 'row'} ${wrap || 'nowrap'}`
}

type FlexFlowVals = Parameters<typeof combinedFlexFlow>[0]

const useFlexFlow = createSystemHook<
  {dir?: 'row' | 'column'; wrap?: 'wrap' | 'nowrap' | boolean},
  {vars: CSSProperties; vals?: FlexFlowVals; prev?: string}
>({
  propNames: flexFlowProps,
  reducer: ({vars, vals, prev}, b, vs) => {
    const wrapRaw = vs.wrap?.[b]
    vals = {
      dir: vs.dir?.[b] || vals?.dir,
      wrap:
        wrapRaw === true
          ? 'wrap'
          : wrapRaw === false
            ? 'nowrap'
            : wrapRaw || vals?.wrap,
    }
    const flexFlow = combinedFlexFlow(vals)
    if (flexFlow && flexFlow !== prev) {
      if (b === '_') b = 'xs'
      vars[`--eg-sys--flex-flow--${b}`] = flexFlow
      prev = flexFlow
    }
    return {vars, vals, prev}
  },
})

type UseFlexFlowProps = Parameters<typeof useFlexFlow>[0]

const useFlex = simpleSystemHook('flex')
type UseFlexProps = Parameters<typeof useFlex>[0]

const useBorder = simpleSystemHook('border', 'border', 'borders')
type UseBorderProps = Parameters<typeof useBorder>[0]

const useMaxWidth = simpleSystemHook('maxWidth', 'max-width')
type UseMaxWidthProps = Parameters<typeof useMaxWidth>[0]

const useWidth = simpleSystemHook('width')
type UseWidthProps = Parameters<typeof useWidth>[0]

const useHeight = simpleSystemHook('height')
type UseHeightProps = Parameters<typeof useHeight>[0]

export interface UseSystemProps
  extends UseAlignContentProps,
    UseAlignItemsProps,
    UseBorderProps,
    UseDisplayProps,
    UseFlexFlowProps,
    UseFlexProps,
    UseGapProps,
    UseGridTemplateColumnsProps,
    UseHeightProps,
    UseJustifyContentProps,
    UseMarginProps,
    UseMaxWidthProps,
    UsePaddingProps,
    UseWidthProps {}

export const useSystem = (props: UseSystemProps): CSSProperties => ({
  ...useAlignContent(props),
  ...useAlignItems(props),
  ...useBorder(props),
  ...useDisplay(props),
  ...useFlexFlow(props),
  ...useFlex(props),
  ...useGap(props),
  ...useGridTemplateColumns(props),
  ...useHeight(props),
  ...useJustifyContent(props),
  ...useMargin(props),
  ...useMaxWidth(props),
  ...usePadding(props),
  ...useWidth(props),
})

export const systemPropNames = [
  ...flexFlowProps,
  ...gapProps,
  ...marginProps,
  ...paddingProps,
  'alignContent',
  'alignItems',
  'border',
  'cols',
  'display',
  'flex',
  'height',
  'justify',
  'maxWidth',
  'width',
] as const
